import {
  Children,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from 'react';

export type Location = Readonly<{
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
}>;

export type NavigateOptions = {
  replace?: boolean;
  state?: unknown;
};

export type NavigateFunction = {
  (to: string, options?: NavigateOptions): void;
  (delta: number): void;
};

type RouterContextValue = {
  location: Location;
  navigate: NavigateFunction;
};

const RouterContext = createContext<RouterContextValue | null>(null);
const ParamsContext = createContext<Readonly<Record<string, string | undefined>>>({});
const memoryOrigin = 'http://pursuitdeck.local';

const locationHref = (location: Location): string =>
  memoryOrigin + location.pathname + location.search + location.hash;

const parseLocation = (to: string, base: string, state: unknown = null): Location => {
  const url = new URL(to, base);
  return {
    pathname: url.pathname || '/',
    search: url.search,
    hash: url.hash,
    state,
  };
};

const useRouter = (): RouterContextValue => {
  const router = useContext(RouterContext);
  if (!router) throw new Error('PursuitDeck routing hooks must be used inside BrowserRouter or MemoryRouter.');
  return router;
};

const readBrowserLocation = (): Location => ({
  pathname: window.location.pathname || '/',
  search: window.location.search,
  hash: window.location.hash,
  state: window.history.state,
});

export const BrowserRouter = ({children}: {children: ReactNode}) => {
  const [location, setLocation] = useState<Location>(readBrowserLocation);

  useEffect(() => {
    const updateFromHistory = () => setLocation(readBrowserLocation());
    window.addEventListener('popstate', updateFromHistory);
    return () => window.removeEventListener('popstate', updateFromHistory);
  }, []);

  const navigate = useCallback<NavigateFunction>((toOrDelta: string | number, options?: NavigateOptions) => {
    if (typeof toOrDelta === 'number') {
      window.history.go(toOrDelta);
      return;
    }

    const url = new URL(toOrDelta, window.location.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      throw new Error('PursuitDeck navigation supports only HTTP(S) locations.');
    }
    if (url.origin !== window.location.origin) {
      window.location.assign(url.href);
      return;
    }

    const next = url.pathname + url.search + url.hash;
    if (options?.replace) {
      window.history.replaceState(options.state ?? null, '', next);
    } else {
      window.history.pushState(options?.state ?? null, '', next);
    }
    setLocation(readBrowserLocation());
  }, []);

  const value = useMemo(() => ({location, navigate}), [location, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
};

type MemoryHistory = {
  entries: Location[];
  index: number;
};

export type MemoryRouterProps = {
  children: ReactNode;
  initialEntries?: readonly string[];
  initialIndex?: number;
};

export const MemoryRouter = ({
  children,
  initialEntries = ['/'],
  initialIndex,
}: MemoryRouterProps) => {
  const [history, setHistory] = useState<MemoryHistory>(() => {
    const entries = (initialEntries.length > 0 ? initialEntries : ['/'])
      .map((entry) => parseLocation(entry, memoryOrigin + '/'));
    const requestedIndex = initialIndex ?? entries.length - 1;
    return {
      entries,
      index: Math.min(Math.max(requestedIndex, 0), entries.length - 1),
    };
  });

  const navigate = useCallback<NavigateFunction>((toOrDelta: string | number, options?: NavigateOptions) => {
    setHistory((current) => {
      if (typeof toOrDelta === 'number') {
        const nextIndex = current.index + toOrDelta;
        return nextIndex < 0 || nextIndex >= current.entries.length
          ? current
          : {...current, index: nextIndex};
      }

      const currentLocation = current.entries[current.index]!;
      const nextLocation = parseLocation(
        toOrDelta,
        locationHref(currentLocation),
        options?.state ?? null,
      );
      if (options?.replace) {
        const entries = [...current.entries];
        entries[current.index] = nextLocation;
        return {entries, index: current.index};
      }

      const entries = [...current.entries.slice(0, current.index + 1), nextLocation];
      return {entries, index: entries.length - 1};
    });
  }, []);

  const location = history.entries[history.index]!;
  const value = useMemo(() => ({location, navigate}), [location, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
};

export const useLocation = (): Location => useRouter().location;
export const useNavigate = (): NavigateFunction => useRouter().navigate;

export const useParams = <
  Params extends Record<string, string | undefined> = Record<string, string | undefined>,
>(): Readonly<Params> => useContext(ParamsContext) as Readonly<Params>;

export type RouteProps = {
  path?: string;
  index?: boolean;
  element?: ReactNode;
};

export const Route = (_props: RouteProps): null => null;

const normalizedPathname = (pathname: string): string => {
  if (!pathname || pathname === '/') return '/';
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
};

const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const matchRoute = (
  route: RouteProps,
  pathname: string,
): Readonly<Record<string, string | undefined>> | null => {
  const normalized = normalizedPathname(pathname);
  if (route.index) return normalized === '/' ? {} : null;
  if (!route.path) return null;
  if (route.path === '*') return {};

  const routeSegments = route.path.replace(/^\/+|\/+$/g, '').split('/');
  const pathSegments = normalized.replace(/^\/+|\/+$/g, '').split('/');
  if (routeSegments.length !== pathSegments.length) return null;

  const params: Record<string, string | undefined> = {};
  for (let index = 0; index < routeSegments.length; index += 1) {
    const expected = routeSegments[index]!;
    const actual = pathSegments[index]!;
    if (expected.startsWith(':')) {
      params[expected.slice(1)] = decodeSegment(actual);
    } else if (expected.toLowerCase() !== decodeSegment(actual).toLowerCase()) {
      return null;
    }
  }
  return params;
};

export const Routes = ({children}: {children: ReactNode}) => {
  const {location} = useRouter();
  for (const child of Children.toArray(children)) {
    if (!isValidElement<RouteProps>(child) || child.type !== Route) continue;
    const params = matchRoute(child.props, location.pathname);
    if (params) {
      return <ParamsContext.Provider value={params}>{child.props.element ?? null}</ParamsContext.Provider>;
    }
  }
  return null;
};

export const Navigate = ({
  to,
  replace = false,
  state,
}: {
  to: string;
  replace?: boolean;
  state?: unknown;
}) => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, {replace, state});
  }, [navigate, replace, state, to]);
  return null;
};

const resolveHref = (to: string, location: Location): string => {
  const url = new URL(to, locationHref(location));
  return url.origin === memoryOrigin
    ? url.pathname + url.search + url.hash
    : url.href;
};

const isPlainInternalClick = (
  event: MouseEvent<HTMLAnchorElement>,
  href: string,
  target: string | undefined,
  download: string | boolean | undefined,
): boolean => {
  if (
    event.defaultPrevented
    || event.button !== 0
    || event.metaKey
    || event.altKey
    || event.ctrlKey
    || event.shiftKey
    || download !== undefined
    || (target && target !== '_self')
  ) {
    return false;
  }

  const url = new URL(href, window.location.href);
  return (url.protocol === 'http:' || url.protocol === 'https:')
    && url.origin === window.location.origin;
};

export type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  to: string;
  replace?: boolean;
  state?: unknown;
};

export const Link = ({
  to,
  replace = false,
  state,
  onClick,
  target,
  download,
  ...anchorProps
}: LinkProps) => {
  const {location, navigate} = useRouter();
  const href = resolveHref(to, location);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (!isPlainInternalClick(event, href, target, download)) return;
    event.preventDefault();
    navigate(href, {replace, state});
  };

  return (
    <a
      {...anchorProps}
      href={href}
      target={target}
      download={download}
      onClick={handleClick}
    />
  );
};

type NavLinkRenderProps = {
  isActive: boolean;
  isPending: false;
};

export type NavLinkProps = Omit<LinkProps, 'children' | 'className' | 'style'> & {
  end?: boolean;
  children?: ReactNode | ((props: NavLinkRenderProps) => ReactNode);
  className?: string | ((props: NavLinkRenderProps) => string | undefined);
  style?: CSSProperties | ((props: NavLinkRenderProps) => CSSProperties | undefined);
};

export const NavLink = ({
  to,
  end = false,
  children,
  className,
  style,
  ...linkProps
}: NavLinkProps) => {
  const location = useLocation();
  const href = resolveHref(to, location);
  const targetPath = normalizedPathname(new URL(href, memoryOrigin).pathname);
  const currentPath = normalizedPathname(location.pathname);
  const comparableTargetPath = targetPath.toLowerCase();
  const comparableCurrentPath = currentPath.toLowerCase();
  const isActive = comparableCurrentPath === comparableTargetPath
    || (!end && comparableTargetPath !== '/' && comparableCurrentPath.startsWith(comparableTargetPath + '/'));
  const renderProps: NavLinkRenderProps = {isActive, isPending: false};

  return (
    <Link
      {...linkProps}
      to={to}
      className={typeof className === 'function' ? className(renderProps) : className}
      style={typeof style === 'function' ? style(renderProps) : style}
      aria-current={isActive ? linkProps['aria-current'] ?? 'page' : undefined}
    >
      {typeof children === 'function' ? children(renderProps) : children}
    </Link>
  );
};

export type URLSearchParamsInit =
  | string
  | readonly (readonly [string, string])[]
  | Record<string, string>
  | URLSearchParams;

export type SetURLSearchParams = (
  nextInit: URLSearchParamsInit | ((previous: URLSearchParams) => URLSearchParamsInit),
  options?: NavigateOptions,
) => void;

const createSearchParams = (init: URLSearchParamsInit = ''): URLSearchParams => {
  if (init instanceof URLSearchParams || typeof init === 'string') return new URLSearchParams(init);
  if (Array.isArray(init)) return new URLSearchParams(init);
  return new URLSearchParams(Object.entries(init));
};

export const useSearchParams = (
  defaultInit?: URLSearchParamsInit,
): [URLSearchParams, SetURLSearchParams] => {
  const {location, navigate} = useRouter();
  const searchParams = useMemo(() => {
    const current = new URLSearchParams(location.search);
    if (current.size > 0 || defaultInit === undefined) return current;
    return createSearchParams(defaultInit);
  }, [defaultInit, location.search]);

  const setSearchParams = useCallback<SetURLSearchParams>((nextInit, options) => {
    const resolved = typeof nextInit === 'function'
      ? nextInit(new URLSearchParams(location.search))
      : nextInit;
    const next = createSearchParams(resolved).toString();
    navigate(location.pathname + (next ? '?' + next : ''), options);
  }, [location.pathname, location.search, navigate]);

  return [searchParams, setSearchParams];
};
